/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import { useEffect, useMemo } from 'react'
import swal from 'sweetalert2'
import { toast } from 'react-toastify'

import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'
import ExifReader from 'exifreader'

import { storageAccountName } from '@functions/api'

const initializeUppy = (options) => {
  const {
    allowedFileTypes,
    callbacks,
    currentUserId,
    fileServiceContainer,
    maxFileSize,
    requestOptions = {},
    type,
    validateFileCount,
  } = options

  let errorList = []
  let selectedFiles = []
  const uploadedList = []

  const { sasToken, container, id: fileServiceContainerId } = fileServiceContainer || {}
  const { serviceJobId } = requestOptions

  const { addRecentlyUploadedBatch, createDirectFn, doneUpload, scanFolderFn, setFilesToUploadCount } = callbacks

  const createDirect = async (entityParams, newList) => {
    await createDirectFn(entityParams).then(({ success, errors, data }) => {
      if (!success && errors){
        toast.warn(errors[0])
      }

      newList.push(data)
    })
  }

  const scanFolder = async (newList) => {
    await scanFolderFn(serviceJobId).then(({ success, errors, data }) => {
      if (!success && errors){
        toast.warn(errors[0])
      }

      newList.push(...data)
    })
  }

  const onComplete = async () => {
    let counter = uploadedList.length
    const newList = []

    function updateList(){
      counter -= 1
      uploadedList.splice(0, uploadedList.length)
      if (counter === 0){
        addRecentlyUploadedBatch(newList, errorList)
        doneUpload(true)
      }
    }

    async function addToNewList(file, callback){
      await createDirect(file.meta, newList)
      callback()
    }

    if (type === 'Resources'){
      await scanFolder(newList)
      addRecentlyUploadedBatch(newList, errorList)
      doneUpload(true)
    } else {
      uploadedList.forEach(async (file) => {
        addToNewList(file, updateList)
      })
    }

    if (!uploadedList.length && errorList.length){
      addRecentlyUploadedBatch(newList, errorList)
      doneUpload(true)
    }
  }

  const validateItemCounts = async (files, uppy) => {
    if (Object.keys(files).length === parseInt(validateFileCount, 10)){
      uppy.upload()
    } else {
      swal.fire({
        title: 'Number of Resources',
        text: `Selected images do not match the number of images for upload. Would you like to proceed with ${Object.keys(files).length}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        confirmButtonColor: '#e2001a',
      }).then(({ value }) => {
        if (value){
          uppy.upload().then(() => {
            setFilesToUploadCount(Object.keys(files).length)
          })
        } else {
          errorList = []
          uppy.reset()
        }
      })
    }
    selectedFiles = []
  }

  const processFile = async (file, uppy) => {
    const filePath = type.toUpperCase() === 'ASSETS' ? `${serviceJobId}/assets/${Date.now()}` : `${serviceJobId}/resources`
    const blobUrl = encodeURI(`https://${storageAccountName}.blob.core.windows.net/${container}/${filePath}/${file.meta.name}`)

    let tags = `userId=${currentUserId}&containerId=${fileServiceContainerId}`
    tags = type.toUpperCase() === 'ASSETS' ? tags : `${tags}&resourceTypeId=${requestOptions.resourceTypeId}`

    uppy.setFileMeta(file.id, {
      ...requestOptions,
      fileServiceContainerId,
      filePath,
      fileName: file.meta.name,
      blobUrl,
    })

    if (file.meta.type.startsWith('image/')){
      const arrayBuffer = await file.data.arrayBuffer()

      try {
        const metaKeys = [
          'ColorSpace',
          'DateTimeOriginal',
          'FNumber',
          'ExposureTime',
          'ImageLength',
          'ImageWidth',
          'ISOSpeedRatings',
          'Make',
          'Model',
          // jpg exif keys has spaces in between
          'Color Space',
          'Image Height',
          'Image Width',
        ]

        const exifTags = ExifReader.load(arrayBuffer)
        const filterExif = Object.fromEntries(
          metaKeys.map(key => [key, exifTags[key]]),
        )

        uppy.setFileState(file.id, {
          xhrUpload: {
            ...file.xhrUpload,
            endpoint: `${blobUrl}?${sasToken}`,
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'x-ms-blob-content-type': file.meta.type,
              'x-ms-meta-size': file.size,
              ...(filterExif?.DateTimeOriginal) && { 'x-ms-meta-datetimeoriginal': filterExif.DateTimeOriginal.description },
              ...(filterExif?.ExposureTime) && { 'x-ms-meta-exposuretime': `ISO ${filterExif.ExposureTime.description}` },
              ...(filterExif?.FNumber) && { 'x-ms-meta-fnumber': filterExif.FNumber.description },
              ...(filterExif?.ISOSpeedRatings) && { 'x-ms-meta-iso': filterExif.ISOSpeedRatings.description },
              ...(filterExif?.Make) && { 'x-ms-meta-make': filterExif.Make.description },
              ...(filterExif?.Model) && { 'x-ms-meta-model': filterExif.Model.description },
              ...(filterExif?.ColorSpace && !filterExif['Color Space']?.value) && { 'x-ms-meta-colorspace': filterExif.ColorSpace.description },
              ...(filterExif['Color Space']?.value) && { 'x-ms-meta-colorspace': filterExif['Color Space'].value },
              ...(filterExif['Image Height']?.value && filterExif['Image Width']?.value && !file.meta.type.endsWith('DNG'))
                && { 'x-ms-meta-dimensions': `${filterExif['Image Width'].value} x ${filterExif['Image Height'].value}` },
              ...(filterExif?.ImageLength && filterExif?.ImageWidth && !file.meta.type.endsWith('DNG')
                && (!filterExif['Image Height']?.value && !filterExif['Image Width']?.value))
                && { 'x-ms-meta-dimensions': `${filterExif.ImageWidth.value} x ${filterExif.ImageLength.value}` },
              'x-ms-tags': tags,
            },
          },
        })
      } catch (error){
        console.error('Error parsing EXIF data:', error)

        uppy.setFileState(file.id, {
          xhrUpload: {
            ...file.xhrUpload,
            endpoint: `${blobUrl}?${sasToken}`,
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'x-ms-blob-content-type': file.meta.type,
              'x-ms-tags': tags,
            },
          },
        })
      }
    } else {
      uppy.setFileState(file.id, {
        xhrUpload: {
          ...file.xhrUpload,
          endpoint: `${blobUrl}?${sasToken}`,
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'x-ms-blob-content-type': file.meta.type,
            'x-ms-tags': tags,
          },
        },
      })
    }
  }

  const uppy = new Uppy({
    id: 'uppyUpload',
    debug: false,
    autoProceed: false,
    restrictions: {
      allowedFileTypes,
      maxFileSize,
    },
    allowMultipleUploadBatches: !parseInt(validateFileCount, 10),
    onBeforeFileAdded: (currentFile) => {
      selectedFiles.push(currentFile)
    },
  })
    .use(XHRUpload, {
      method: 'PUT',
      formData: false,
      fieldName: 'FormFile',
      getResponseError(){ },
      timeout: 0,
    })
    .on('restriction-failed', (file, error) => {
      if (error.message === 'Cannot add more files'){
        toast.warning(error.message)
      } else {
        errorList.push({ ...file, errorType: 'restricted', errorMessage: error.message })
      }
    })
    .on('files-added', async (files) => {
      Promise.all(Object.values(files).map(file => processFile(file, uppy)))
        .then(async () => {
          if (validateFileCount){
            await validateItemCounts(selectedFiles, uppy)
          } else {
            uppy.upload()
          }
        })
    })
    .on('upload-success', (file) => {
      uploadedList.push(file)
    })
    .on('upload-error', (file) => {
      errorList.push({ ...file, errorType: 'failed', errorMessage: 'Failed to Upload' })
    })
    .on('complete', async () => {
      await onComplete()
      uppy.reset()
    })

  return uppy
}

const checkRequiredOptions = (options = {}) => {
  const requiredOptions = ['allowedFileTypes', 'requestOptions', 'fileServiceContainer']

  return requiredOptions.every(key => Object.keys(options).includes(key))
}

function useUppyUpload(options = {}){
  const uppy = useMemo(() => {
    if (checkRequiredOptions(options)){
      return initializeUppy(options)
    }
  })

  useEffect(() => () => (uppy ? uppy.close() : null))

  return uppy
}

export default useUppyUpload
